/**
 * Public ticket-status page.
 *
 * Reached by scanning the printed QR code (URL = /ticket-status/{ticket_id}).
 * Polls /api/tickets/{id}/status-public — backend strips ALL PII.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { warnDev } from "@/lib/log";
import { Loader2, CheckCircle2, Hourglass, PlayCircle, Ban, UserX, AlertCircle, MapPin, Clock } from "lucide-react";

const STATUS_META = {
  waiting:   { label: "En attente",  icon: Hourglass,    color: "#64748B", tone: "bg-slate-100 text-slate-700" },
  called:    { label: "Appelé",      icon: AlertCircle,  color: "#DC2626", tone: "bg-red-50 text-[#DC2626]" },
  serving:   { label: "En cours",    icon: PlayCircle,   color: "#2563EB", tone: "bg-blue-50 text-blue-700" },
  completed: { label: "Terminé",     icon: CheckCircle2, color: "#16A34A", tone: "bg-green-50 text-[#16A34A]" },
  missed:    { label: "Absent",      icon: UserX,        color: "#EA580C", tone: "bg-orange-50 text-orange-700" },
  cancelled: { label: "Annulé",      icon: Ban,          color: "#94A3B8", tone: "bg-slate-100 text-slate-500" },
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function TicketStatusPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}/status-public`);
      setTicket(data);
      setError(null);
    } catch (e) {
      warnDev("ticket-status fetch", e);
      setError(e.response?.status === 404 ? "Ce ticket est introuvable." : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000); // poll every 10s
    return () => clearInterval(t);
  }, [refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <Loader2 className="w-8 h-8 text-[#DC2626] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <img src={LOGO_URL} alt="MAMA" className="w-16 h-16 mb-6" />
        <div className="bg-white border border-slate-200 rounded-2xl px-8 py-10 max-w-md text-center shadow-sm">
          <AlertCircle className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">Ticket introuvable</h1>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[ticket.status] || STATUS_META.waiting;
  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-white flex flex-col">
      {/* Brand header */}
      <header className="bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3">
        <img src={LOGO_URL} alt="MAMA" className="w-11 h-11" />
        <div>
          <div className="font-display font-bold text-slate-900">MAMA</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Statut de votre ticket</div>
        </div>
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-5 py-8 space-y-5" data-testid="ticket-status-page">
        {/* Hero ticket card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-2">Numéro</div>
          <div className="font-display font-black text-[#DC2626] text-7xl tracking-tighter leading-none"
               data-testid="ticket-status-code">
            {ticket.full_ticket_code}
          </div>

          <div className="inline-flex items-center mt-4 px-4 py-1.5 rounded-full text-sm font-bold"
               style={{ background: `${ticket.color}1A`, color: ticket.color }}
               data-testid="ticket-status-category">
            {ticket.category_label}
          </div>

          <div className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full ${meta.tone}`}
               data-testid="ticket-status-state">
            <Icon className="w-4 h-4" />
            <span className="font-bold tracking-wide">{meta.label}</span>
          </div>

          {ticket.status === "waiting" && ticket.position && (
            <div className="mt-5 border-t border-slate-100 pt-5">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">Position dans la file</div>
              <div className="font-display font-black text-4xl text-slate-900 mt-1 tabular-nums"
                   data-testid="ticket-status-position">
                {ticket.position}
                <span className="text-base text-slate-400 font-normal ml-1">/ avant vous : {ticket.position - 1}</span>
              </div>
            </div>
          )}

          {(ticket.status === "called" || ticket.status === "serving") && ticket.assigned_counter_name && (
            <div className="mt-5 border-t border-slate-100 pt-5">
              <div className="text-[11px] uppercase tracking-widest text-slate-400">Présentez-vous au</div>
              <div className="font-display font-black text-3xl text-[#16A34A] mt-1"
                   data-testid="ticket-status-counter">
                {ticket.assigned_counter_name}
              </div>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
          <MetaRow icon={MapPin}
                   label="Agence"
                   value={ticket.agency_name || ticket.agency_code} />
          <MetaRow icon={Clock}
                   label="Émis le"
                   value={formatDateTime(ticket.created_at)} />
        </div>

        {/* Privacy notice */}
        <div className="text-center text-xs text-slate-400 px-4 leading-relaxed">
          Cette page se met à jour automatiquement. Aucune donnée personnelle n'est affichée.
          <br />
          <Link to="/" className="text-[#DC2626] hover:underline">MAMA — Mutuelle d'Assurance Malagasy</Link>
        </div>
      </main>
    </div>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="flex-1 flex items-baseline justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-sm font-medium text-slate-800 text-right">{value}</div>
      </div>
    </div>
  );
}
